import type {Moment} from 'moment-timezone'
import moment from 'moment-timezone'
import {mmap} from '@freckle/maybe'
import type {NonEmptyArray} from '@freckle/non-empty'
import {mkNonEmpty} from '@freckle/non-empty'
import type {FactPracticeOperationT} from '@freckle/educator-entities/ts/math/fact-practice/helpers/fact-practice-operation'
import type {FactPracticeSnapshotAttrs} from '@freckle/educator-entities/ts/math/fact-practice/models/math-fact-practice-snapshot'
import type {DehydratedDomain} from '../rl-domain'
import type {DehydratedSkill} from './../../../common/models/rl-skill'
import type {DehydratedStandard} from './../../../common/models/rl-standard'
import type {DehydratedMathAssessmentTopicT} from './../../../math/assessment/types/assessment-topic'
import {fromOpenApiUglyGrade} from './grade'
import type {Metadata} from './metadata'
import type {SessionTypeT} from './session-type'
import {fromOpenApiSkill} from './skill'

/**
 * Math Adaptive
 */

export type MathAdaptiveMetadataT = {
  domain: DehydratedDomain
  currentStandard: DehydratedStandard | undefined | null
  startsAt: Moment | undefined | null
}

export function fromOpenApiMathAdaptiveMetadata(
  x: Metadata<'math_adaptive'>
): MathAdaptiveMetadataT {
  const {domain, currentStandard} = x
  return {
    domain,
    currentStandard,
    startsAt: mmap(moment, x.startsAt)
  }
}

/**
 * Math Targeted
 */

export type MathTargetedMetadataT = {
  standard: {id: string}
  skill: DehydratedSkill | undefined | null
  startsAt: Moment | null
  originalAssignmentId: number | undefined | null
  numQuestionsAnswered: number | undefined | null
  totalWorth: number | undefined | null
  completedWorth: number | undefined | null
  isSupported: boolean | undefined | null
  numQuestions: number
  type: SessionTypeT
}

export function fromOpenApiMathTargetedMetadata(
  x: Metadata<'math_targeted'> & {standard: NonNullable<Metadata<'math_targeted'>['standard']>}
): MathTargetedMetadataT {
  const {completedWorth, isSupported, numQuestions, originalAssignmentId, type, standard} = x
  return {
    completedWorth,
    isSupported,
    numQuestions,
    originalAssignmentId,
    type,
    standard,
    skill: mmap(fromOpenApiSkill, x.skill),
    startsAt: x.startsAt !== undefined && x.startsAt !== null ? moment(x.startsAt) : null,
    numQuestionsAnswered: null,
    totalWorth: null
  }
}

/**
 * Math Focus Skills Practice
 */

export type MathFocusSkillsPracticeMetadataT = {
  startsAt: Moment | null
  originalAssignmentId: number | undefined | null
  completedWorth: number | undefined | null
  isSupported: boolean | undefined | null
  numQuestions: number
  type: SessionTypeT
}

export function fromOpenApiFocusSkillsPracticeMetadata(
  x: Metadata<'math_targeted'>
): MathFocusSkillsPracticeMetadataT {
  const {completedWorth, isSupported, numQuestions, originalAssignmentId, type} = x
  return {
    completedWorth,
    isSupported,
    numQuestions,
    originalAssignmentId,
    type,
    startsAt: x.startsAt !== undefined && x.startsAt !== null ? moment(x.startsAt) : null
  }
}

/**
 * Math Assessments
 */

export type MathAssessmentMetadataT = {
  topic: DehydratedMathAssessmentTopicT
  startsAt: Moment | undefined | null
}

export function fromOpenApiMathAssessmentMetadata(
  x: Metadata<'math_assessments'>
): MathAssessmentMetadataT {
  return {
    topic: fromOpenApiMathAssessmentTopic(x.topic),
    startsAt: mmap(moment, x.startsAt)
  }
}

export function fromOpenApiMathAssessmentTopic(
  x: Metadata<'math_assessments'>['topic']
): DehydratedMathAssessmentTopicT {
  return {
    schoolGrade: fromOpenApiUglyGrade(x.schoolGrade),
    domainStandards: x.domainStandards
  }
}

/**
 * Math Ibls
 */

export type MathIblMetadataT = {
  lessonId: number
  day: number
  title: string
  standard: DehydratedStandard
}

export function fromOpenApiMathIblMetadata(x: Metadata<'math_ibls'>): MathIblMetadataT {
  return x
}

/**
 * Math Number Basics
 */

export type MathNumberBasicsMetadataT = {
  skillCode: string
}

/**
 * Math Fact Practice
 */

type MathFactPracticeOperationSnapshotMetadataT = {
  operation: FactPracticeOperationT
  snapshot: FactPracticeSnapshotAttrs | undefined | null
}

export type MathFactPracticeMetadataT = {
  operations: NonEmptyArray<FactPracticeOperationT>
  snapshots: Array<MathFactPracticeOperationSnapshotMetadataT>
  startsAt: Moment | undefined | null
}

export function fromOpenApiMathFactPracticeMetadata(
  x: Metadata<'math_fact_practice'>
): MathFactPracticeMetadataT {
  return {
    operations: (function () {
      const xs = mkNonEmpty(x.operations)
      if (xs === null) {
        throw new Error('MathFactPractice session metadata: operations list is empty')
      }
      return xs
    })(),
    snapshots: x.snapshots.map(y => {
      return {
        operation: y.operation,
        snapshot: mmap(fromOpenApiMathFactPracticeSnapshot, y.snapshot)
      }
    }),
    startsAt: mmap(moment, x.startsAt)
  }
}

export function fromOpenApiMathFactPracticeSnapshot(
  x: NonNullable<Metadata<'math_fact_practice'>['snapshots'][number]['snapshot']>
): FactPracticeSnapshotAttrs {
  const {id, studentId, assignmentSessionId, operation, level, accuracy, numQuestionsAnswered} = x
  return {
    id,
    studentId,
    assignmentSessionId,
    operation,
    level,
    accuracy,
    numQuestionsAnswered,
    createdAt: moment(x.createdAt)
  }
}

/**
 * Math Depth of Knowledge
 */

export type MathDepthOfKnowledgeMetadataT = {
  questionSetUuid: string
}

/**
 * Math Targeted Depth of Knowledge
 */

export type MathTargetedDepthOfKnowledgeMetadataT = {
  questionSetUuid: string
  standard: DehydratedStandard
}

export function fromOpenApiMathTargetedDepthOfKnowledgePracticeMetadata(
  x: Metadata<'math_targeted_depth_of_knowledge_practice'>
): MathTargetedDepthOfKnowledgeMetadataT {
  return x
}
